import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../utils/api";
import QRCode from "react-qr-code";
import copy from "copy-to-clipboard";
import { useAuth } from "../context/AuthContext";
import {
  FaQrcode,
  FaCopy,
  FaExclamationTriangle,
  FaCheckCircle,
  FaClock,
  FaTimesCircle,
  FaExternalLinkAlt,
  FaWallet,
  FaShieldAlt,
  FaBolt,
  FaCoins,
  FaHistory,
  FaSpinner,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";

const Deposit = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [depositAddress, setDepositAddress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deposits, setDeposits] = useState([]);
  const [showSteps, setShowSteps] = useState(false);
  const [copied, setCopied] = useState(false);
  const pollingIntervalRef = useRef(null);
  const previousDepositsRef = useRef([]);
  const wasActivatedRef = useRef(user?.isActivated || false);
  const isRedirectingRef = useRef(false);

  // Redirect activated users to dashboard
  useEffect(() => {
    if (user?.isActivated) {
      toast.info("Your account is already activated!");
      navigate("/dashboard");
    }
  }, [user, navigate]);

  useEffect(() => {
    fetchDepositAddress();
    fetchDepositHistory();
    startPolling();
    setTimeout(() => {
      checkForDepositUpdates();
    }, 2000);
    return () => {
      stopPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user?.isActivated) {
      wasActivatedRef.current = true;
    }
  }, [user?.isActivated]);

  const startPolling = () => {
    if (pollingIntervalRef.current) return;
    pollingIntervalRef.current = setInterval(() => {
      checkForDepositUpdates();
    }, 5000);
  };

  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  const checkForDepositUpdates = async () => {
    if (isRedirectingRef.current) return;
    try {
      const response = await api.get("/deposits/history");
      const newDeposits = response.data.data.deposits;
      let shouldRedirect = false;

      const hasConfirmedDeposit = newDeposits.some(
        (dep) => dep.status === "confirmed",
      );
      if (hasConfirmedDeposit) shouldRedirect = true;

      if (previousDepositsRef.current.length > 0) {
        newDeposits.forEach((newDep) => {
          const oldDep = previousDepositsRef.current.find(
            (d) => d._id === newDep._id,
          );
          if (!oldDep) {
            toast.success(`New deposit detected: ${newDep.amountUSD} USDT`, {
              autoClose: 5000,
            });
            refreshUser();
          } else if (oldDep.status !== newDep.status) {
            if (newDep.status === "confirmed") {
              toast.success(`Deposit confirmed: ${newDep.amountUSD} USDT`, {
                autoClose: 5000,
              });
              refreshUser();
            } else if (newDep.status === "failed") {
              toast.error(`Deposit failed: ${newDep.amountUSD} USDT`, {
                autoClose: 5000,
              });
            }
          }
        });
      }

      if (shouldRedirect && !isRedirectingRef.current) {
        isRedirectingRef.current = true;
        await refreshUser();
        toast.info("Redirecting to dashboard...", { autoClose: 2000 });
        stopPolling();
        setTimeout(() => {
          navigate("/dashboard");
        }, 2000);
      }

      previousDepositsRef.current = newDeposits;
      setDeposits(newDeposits);
    } catch (error) {
      console.error("Failed to check deposit updates:", error);
    }
  };

  const fetchDepositAddress = async () => {
    try {
      const response = await api.get("/deposits/address");
      setDepositAddress(response.data.data);
      setLoading(false);
    } catch (error) {
      toast.error("Failed to load deposit address");
      setLoading(false);
    }
  };

  const fetchDepositHistory = async () => {
    try {
      const response = await api.get("/deposits/history");
      const fetchedDeposits = response.data.data.deposits;
      setDeposits(fetchedDeposits);
      previousDepositsRef.current = fetchedDeposits;
    } catch (error) {
      console.error("Failed to load deposit history");
    }
  };

  const handleCopyAddress = () => {
    if (depositAddress) {
      copy(depositAddress.address);
      setCopied(true);
      toast.success("Address copied!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "confirmed":
        return <FaCheckCircle className="text-emerald-400" />;
      case "pending":
        return <FaClock className="text-amber-400 animate-pulse" />;
      case "failed":
        return <FaTimesCircle className="text-rose-400" />;
      default:
        return <FaClock className="text-slate-400" />;
    }
  };

  const getStatusBg = (status) => {
    switch (status) {
      case "confirmed":
        return "bg-emerald-500/20 border-emerald-500/30";
      case "pending":
        return "bg-amber-500/20 border-amber-500/30";
      case "failed":
        return "bg-rose-500/20 border-rose-500/30";
      default:
        return "bg-white/5 border-white/10";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <FaSpinner className="text-2xl text-emerald-400 animate-spin" />
      </div>
    );
  }

  if (user?.isActivated) return null;

  if (!depositAddress) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4">
        <p className="text-rose-300 text-sm text-center">
          Failed to load deposit address. Please refresh.
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 pb-24">
      {/* ── Hero ── */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-700" />
        <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/4" />
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/5 rounded-full translate-y-1/3 -translate-x-1/4" />

        <div className="relative px-4 pt-6 pb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-11 h-11 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center border border-white/30">
              <FaWallet className="text-lg text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">Deposit USDT</h1>
              <p className="text-emerald-100 text-xs">
                BEP20 Network · Auto-Detection
              </p>
            </div>
          </div>

          {/* Scrollable stat chips */}
          <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-1 -mx-4 px-4">
            {[
              {
                icon: FaBolt,
                label: "Speed",
                value: "~3 min",
                color: "text-yellow-300",
              },
              {
                icon: FaShieldAlt,
                label: "Secure",
                value: "Encrypted",
                color: "text-blue-300",
              },
              {
                icon: FaCoins,
                label: "Min",
                value: "$10",
                color: "text-amber-300",
              },
              {
                icon: FaBolt,
                label: "Activation",
                value: "$50",
                color: "text-cyan-300",
              },
            ].map((s, i) => (
              <div
                key={i}
                className="flex-shrink-0 flex items-center gap-2 bg-white/10 backdrop-blur border border-white/20 rounded-full px-3 py-1.5"
              >
                <s.icon className={`text-xs ${s.color}`} />
                <span className="text-[10px] text-white/60">{s.label}</span>
                <span className="text-xs text-white font-bold">{s.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Content ── */}
      <div className="px-4 -mt-2 space-y-3">
        {/* QR Code Card */}
        <div className="bg-white/[0.03] border border-white/[0.06] rounded-2xl p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <FaQrcode className="text-sm text-cyan-400" />
              <span className="text-sm font-semibold text-white">
                Scan to Deposit
              </span>
            </div>
            <span className="text-[10px] font-semibold text-cyan-300 bg-cyan-500/20 px-2 py-0.5 rounded-full border border-cyan-500/30">
              BEP20
            </span>
          </div>

          {/* QR centered */}
          <div className="flex justify-center mb-4">
            <div className="bg-white p-3 rounded-xl">
              <QRCode value={depositAddress.address} size={160} />
            </div>
          </div>

          {/* Address + Copy */}
          <div className="relative mb-3">
            <input
              type="text"
              value={depositAddress.address}
              readOnly
              className="w-full px-3 py-3 pr-20 rounded-xl border border-white/10 bg-slate-900/60 text-slate-200 font-mono text-[11px] tracking-tight"
            />
            <button
              onClick={handleCopyAddress}
              className={`absolute right-1.5 top-1/2 -translate-y-1/2 flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold transition active:scale-95 ${
                copied ?
                  "bg-emerald-500 text-white"
                : "bg-gradient-to-r from-cyan-500 to-violet-500 text-white"
              }`}
            >
              {copied ?
                <>
                  <FaCheckCircle className="text-[10px]" />
                  Copied
                </>
              : <>
                  <FaCopy className="text-[10px]" />
                  Copy
                </>
              }
            </button>
          </div>

          {/* Network details row */}
          <div className="grid grid-cols-4 gap-2">
            {[
              { label: "Network", value: depositAddress.network },
              { label: "Token", value: depositAddress.token },
              { label: "Balance", value: `${depositAddress.balance}` },
              {
                label: "Gas",
                value: parseFloat(depositAddress.bnbBalance).toFixed(4),
              },
            ].map((d, i) => (
              <div
                key={i}
                className="bg-white/[0.03] border border-white/[0.06] rounded-lg p-2 text-center"
              >
                <p className="text-[9px] text-slate-500 uppercase tracking-wide">
                  {d.label}
                </p>
                <p className="text-[11px] font-bold text-white mt-0.5 truncate">
                  {d.value}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* How to Deposit (collapsible) */}
        <button
          onClick={() => setShowSteps(!showSteps)}
          className="w-full bg-white/[0.03] border border-white/[0.06] rounded-2xl p-4 active:scale-[0.98] transition-all"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 bg-cyan-500/20 rounded-lg flex items-center justify-center">
                <FaQrcode className="text-xs text-cyan-400" />
              </div>
              <span className="text-sm font-semibold text-white">
                How to Deposit
              </span>
            </div>
            {showSteps ?
              <FaChevronUp className="text-xs text-slate-400" />
            : <FaChevronDown className="text-xs text-slate-400" />}
          </div>

          {showSteps && (
            <div
              className="mt-3 space-y-2 text-left"
              onClick={(e) => e.stopPropagation()}
            >
              {[
                "Open your crypto wallet app",
                "Select USDT on BEP20 network",
                "Scan QR code or copy address above",
                "Send USDT (min $10, activation $50)",
                "System will auto-detect your deposit",
              ].map((step, i) => (
                <div key={i} className="flex items-center gap-2.5">
                  <div className="w-5 h-5 rounded-full bg-cyan-500/30 border border-cyan-500/40 flex items-center justify-center flex-shrink-0">
                    <span className="text-[9px] font-bold text-cyan-200">
                      {i + 1}
                    </span>
                  </div>
                  <span className="text-xs text-slate-300">{step}</span>
                </div>
              ))}
            </div>
          )}
        </button>

        {/* Important Notes */}
        <div className="bg-amber-500/[0.06] border border-amber-500/20 rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-2.5">
            <FaExclamationTriangle className="text-xs text-amber-400" />
            <span className="text-xs font-semibold text-amber-200">
              Important
            </span>
          </div>
          <div className="space-y-1.5">
            {[
              "Only send USDT on BEP20 (BSC) network",
              "Minimum deposit: $10 USDT",
              "Deposits detected & credited automatically",
              "No manual confirmation needed",
              "Do not send other tokens to this address",
            ].map((note, i) => (
              <div key={i} className="flex items-start gap-2">
                <div className="w-1 h-1 rounded-full bg-amber-400/60 mt-1.5 flex-shrink-0" />
                <span className="text-[11px] text-slate-400">{note}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ── Deposit History ── */}
        <div className="bg-white/[0.03] border border-white/[0.06] rounded-2xl p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <FaHistory className="text-sm text-cyan-400" />
              <span className="text-sm font-semibold text-white">History</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1.5 bg-emerald-500/15 border border-emerald-500/25 rounded-full px-2.5 py-1">
                <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" />
                <span className="text-[10px] text-emerald-300 font-medium">
                  Live
                </span>
              </div>
              {deposits.length > 0 && (
                <span className="text-[10px] text-slate-500 bg-white/5 px-2 py-0.5 rounded-full">
                  {deposits.length}
                </span>
              )}
            </div>
          </div>

          {deposits.length === 0 ?
            <div className="text-center py-10">
              <div className="w-12 h-12 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-3 border border-white/10">
                <FaWallet className="text-lg text-slate-600" />
              </div>
              <p className="text-xs text-slate-400 font-medium">
                No deposits yet
              </p>
              <p className="text-[10px] text-slate-600 mt-0.5">
                History will appear here
              </p>
            </div>
          : <div className="space-y-2">
              {deposits.map((deposit) => (
                <div
                  key={deposit._id}
                  className={`flex items-center gap-3 p-3 rounded-xl border transition ${getStatusBg(
                    deposit.status,
                  )}`}
                >
                  {/* Status icon */}
                  <div className="w-9 h-9 rounded-xl bg-white/5 flex items-center justify-center flex-shrink-0">
                    {getStatusIcon(deposit.status)}
                  </div>

                  {/* Details */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-bold text-white">
                        ${deposit.amountUSD.toFixed(2)}
                      </span>
                      <span
                        className={`text-[10px] font-semibold capitalize px-2 py-0.5 rounded-full ${
                          deposit.status === "confirmed" ?
                            "text-emerald-300 bg-emerald-500/20"
                          : deposit.status === "pending" ?
                            "text-amber-300 bg-amber-500/20"
                          : "text-rose-300 bg-rose-500/20"
                        }`}
                      >
                        {deposit.status}
                      </span>
                    </div>
                    <div className="flex items-center justify-between mt-1">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] text-slate-500">
                          {new Date(deposit.createdAt).toLocaleDateString()}{" "}
                          {new Date(deposit.createdAt).toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </span>
                        {deposit.isActivationPayment && (
                          <span className="text-[9px] text-cyan-400 bg-cyan-500/15 px-1.5 py-0.5 rounded-full font-semibold">
                            ACTIVATION
                          </span>
                        )}
                      </div>
                      <a
                        href={`https://bscscan.com/tx/${deposit.txHash}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-[10px] text-cyan-400 font-mono"
                      >
                        {deposit.txHash.slice(0, 6)}...
                        {deposit.txHash.slice(-4)}
                        <FaExternalLinkAlt className="text-[8px] opacity-50" />
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          }
        </div>

        {/* Security footer */}
        <div className="flex items-center justify-center gap-2 py-4 pb-6">
          <FaShieldAlt className="text-[10px] text-emerald-500/50" />
          <span className="text-[10px] text-slate-600">
            Bank-level encryption · Blockchain secured
          </span>
        </div>
      </div>
    </div>
  );
};

export default Deposit;
